/* eslint-disable @typescript-eslint/camelcase */
// SLS 日志契约使用 snake_case 字段名。
import { FunctionalComponent, h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { SessionProduct } from '../mock/types';
import { OfferPrice } from './ProductCard';
import { isWeiBo, isWeiXinAndQQ } from '../../../utils/utils';
import { jumpHandle } from '../../../utils/Config';
import { resolvePurchaseLink, PurchaseLinks } from '../dataSource';
import { trackProductEvent } from '../../../utils/liveAnalytics';

interface Props {
  product: SessionProduct | null;
  anchorName: string;
  /** D04：当前列表是否已是来源场次（true=只关弹层保留位置） */
  sameSession?: boolean;
  onClose: () => void;
  /** D04：查看本场清单（跳来源场次） */
  onViewSession?: (p: SessionProduct) => void;
}

const STATUS_CHIP: Record<string, { cls: string; label: string }> = {
  living: { cls: 'vp-chip living', label: '直播中' },
  pending: { cls: 'vp-chip pending', label: '待开播' },
  ended: { cls: 'vp-chip ended', label: '已结束' },
};

/** 隐藏 input 复制（老 H5 同款，微信内核可用） */
function copyText(text: string): boolean {
  try {
    const input = document.createElement('input');
    input.setAttribute('value', text);
    input.setAttribute('readonly', 'readonly');
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(input);
    return ok;
  } catch {
    return false;
  }
}

/** 商品详情弹层（D01–D03）：
 * - 购买入口：微博开 scheme；微信/QQ 有口令则复制，无口令直接打开商品链接
 * - D06 演示：所选规格失效 T27 内嵌提示，重选可恢复
 */
const DetailModal: FunctionalComponent<Props> = ({ product, anchorName, sameSession, onClose, onViewSession }) => {
  const [specId, setSpecId] = useState<string | null>(null);
  const [specInvalid, setSpecInvalid] = useState(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [freshLinks, setFreshLinks] = useState<PurchaseLinks | null>(null);
  const purchaseRequest = useRef(0);
  const purchaseBusy = useRef(false);
  const activeProduct = useRef(product);
  activeProduct.current = product;
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const close = useCallback((): void => {
    purchaseRequest.current++;
    purchaseBusy.current = false;
    onClose();
  }, [onClose]);

  useEffect(() => {
    purchaseRequest.current++;
    purchaseBusy.current = false;
    setPurchaseLoading(false);
    setFreshLinks(null);
    setCopyToast(null);
    if (product) {
      setSpecId(product.offers[0]?.specId ?? null);
      setSpecInvalid(false);
      restoreFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { purchaseRequest.current++; document.body.style.overflow = ''; };
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [product, close]);

  if (!product) return null;
  const s = product.session;
  const current = product.offers.find((o) => o.specId === specId) ?? (specInvalid ? null : product.offers[0]);
  const dateLabel = /^\d{4}-\d{2}-\d{2}$/.test(s.date)
    ? `${parseInt(s.date.slice(5, 7), 10)}月${parseInt(s.date.slice(8), 10)}日` : '';
  const chip = STATUS_CHIP[s.status];

  /** 购买入口：微博唤起；微信/QQ 有口令则复制，其余直接打开商品链接。 */
  const openPurchase = (links: PurchaseLinks, refreshed = false): void => {
    if (isWeiBo()) {
      const scheme = links.buyScheme || (links.buyUrl ? `taobao:${links.buyUrl.slice(links.buyUrl.indexOf(':') + 1)}` : '');
      if (scheme && links.buyUrl) jumpHandle(scheme, links.buyUrl);
      else if (links.buyUrl) window.open(links.buyUrl);
      return;
    }
    if (isWeiXinAndQQ()) {
      const content = (links.taotoken ?? '').trim();
      if (!content) {
        if (links.buyUrl) window.open(links.buyUrl);
        return;
      }
      const ok = copyText(content);
      setCopyToast(ok ? '亲，口令复制成功啦，快打开淘宝抢购吧！' : refreshed ? '复制失败，请再次点击复制口令购买' : '复制失败，请长按商品链接手动复制');
      setTimeout(() => setCopyToast(null), 2000);
      return;
    }
    if (links.buyUrl) {
      if (refreshed) window.open(links.buyUrl, '_blank');
      else window.open(links.buyUrl);
    }
  };
  const buy = async (): Promise<void> => {
    if (purchaseBusy.current) return;
    const links = freshLinks || product;
    trackProductEvent('product_buy_click', product, {
      anchor_name: anchorName, spec_id: current?.specId ?? '',
      purchase_method: product.purchaseRequiresRefresh && !freshLinks ? 'resolve'
        : isWeiBo() ? 'scheme' : isWeiXinAndQQ() && links.taotoken?.trim() ? 'token' : 'url',
    });
    if (!product.purchaseRequiresRefresh || freshLinks) {
      openPurchase(links, !!freshLinks);
      return;
    }
    const request = ++purchaseRequest.current;
    purchaseBusy.current = true;
    setPurchaseLoading(true);
    setCopyToast(null);
    try {
      const links = await resolvePurchaseLink(product.sessionProductId);
      if (request !== purchaseRequest.current || activeProduct.current !== product) return;
      setFreshLinks(links);
      openPurchase(links, true);
    } catch (error: any) {
      if (request === purchaseRequest.current && activeProduct.current === product) {
        setCopyToast(error.message);
      }
    } finally {
      if (request === purchaseRequest.current && activeProduct.current === product) {
        purchaseBusy.current = false;
        setPurchaseLoading(false);
      }
    }
  };
  const buyAvailable = !!(product.taotoken || product.buyUrl || product.buyScheme || (product.purchaseRequiresRefresh && product.itemId));

  return (
    <div>
      <div class="vp-mask" onClick={close} />
      <div class="vp-sheet vp-sheet-detail" role="dialog" aria-label={`商品详情 ${product.itemName}`}>
        <div class="vp-sheet-head">
          <b class="vp-sheet-title">商品预告详情</b>
          <button class="vp-sheet-close" onClick={close} aria-label="关闭">×</button>
        </div>

        <div class="vp-sheet-body">
          {specInvalid && (
            <div class="vp-detail-invalid">当前规格已失效，请重新选择（T27）</div>
          )}

          <div class="vp-detail-top">
            <img src={product.image} alt={product.itemName} />
            <div>
              <div class="vp-detail-meta">{anchorName ? `${anchorName} · ` : ''}{product.linkNo}号链接</div>
              <b class="vp-detail-name">{product.itemName}</b>
              <div class="vp-detail-meta">{dateLabel} {s.startTime} · {s.theme}</div>
              <div class="vp-detail-status">
                <span class={chip.cls}>{chip.label}</span>
              </div>
            </div>
          </div>

          <div class="vp-detail-sec">
            <b class="vp-detail-sec-title">选择规格</b>
            <div class="vp-specchips">
              {product.offers.map((o) => (
                <button
                  key={o.specId}
                  class={`vp-specchip${o.specId === specId ? ' selected' : ''}`}
                  onClick={() => { setSpecId(o.specId); setSpecInvalid(false); }}
                >
                  <span>{o.name}</span>
                  <b>{o.price ? `¥${o.price}` : '待公布'}</b>
                </button>
              ))}
            </div>
          </div>

          <div class="vp-detail-sec">
            <b class="vp-detail-sec-title">该规格赠品</b>
            {current?.gift.has ? (
              <div class="vp-gift">
                {current.gift.images[0] ? <img class="vp-gift-img" src={current.gift.images[0]} alt="" /> : null}
                <span class="vp-gift-desc">{current.gift.desc}</span>
              </div>
            ) : (
              <div class="vp-detail-none">{specInvalid ? '待选择有效规格' : '本规格暂无赠品'}</div>
            )}
          </div>

          <div class="vp-detail-grid">
            <div>
              <span class="k">预告价格</span>
              {current ? <OfferPrice price={current.price} unit={current.toHandUnit} /> : <span class="vp-detail-none">请选择规格</span>}
            </div>
            <div><span class="k">到手条件</span><span class="v">{current?.condition ?? '—'}</span></div>
            <div><span class="k">信息状态</span><span class="v">预告信息 · 开播前可能调整</span></div>
          </div>

          <div class="vp-detail-note">商品、价格及赠品可能调整，请以直播间最终信息为准。</div>
        </div>

        <div class="vp-sheet-footer">
          <div class="vp-detail-actions">
            {buyAvailable && (
              <button class="vp-card-btn solid" onClick={buy} disabled={purchaseLoading} aria-busy={purchaseLoading}>
                {purchaseLoading ? '正在获取购买链接…' : isWeiXinAndQQ() && (freshLinks || product).taotoken?.trim() ? '复制口令购买' : '立即购买'}
              </button>
            )}
            {onViewSession && s.id && s.anchorId > 0 && (
              <button class="vp-card-btn ghost" onClick={() => {
                close(); // Leaving the detail also invalidates an in-flight purchase result.
                if (!sameSession) onViewSession(product);
              }}>查看本场清单</button>
            )}
          </div>

          {copyToast && <div class="vp-toast">{copyToast}</div>}

        </div>
      </div>
    </div>
  );
};

export default DetailModal;
